import React, { useEffect, useState } from 'react';
import {
  DocumentTextIcon,
  ArrowDownTrayIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { documentsAPI } from '../services/api';
import { format } from 'date-fns';

interface Document {
  id: number;
  filename: string;
  format: string;
  chat_id: number;
  created_at: string;
}

export default function Documents() {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDocuments();
  }, []);

  const loadDocuments = async () => {
    try {
      const response = await documentsAPI.list();
      setDocuments(response.data);
    } catch (error) {
      console.error('Failed to load documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (doc: Document) => {
    try {
      const response = await documentsAPI.download(doc.id);
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.filename;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download document:', error);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      await documentsAPI.delete(id);
      loadDocuments();
    } catch (error) {
      console.error('Failed to delete document:', error);
    }
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto p-6">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-2xl font-bold text-surface-100 mb-6">Documents</h1>

        {documents.length === 0 ? (
          <div className="text-center py-12">
            <DocumentTextIcon className="w-12 h-12 text-surface-600 mx-auto mb-4" />
            <p className="text-surface-400">No documents yet</p>
            <p className="text-surface-500 text-sm mt-2">
              Documents are created when you ask Quorum to generate PDFs or
              markdown files
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {documents.map((doc) => (
              <div
                key={doc.id}
                className="card flex items-center justify-between"
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-surface-700 flex items-center justify-center">
                    <DocumentTextIcon className="w-5 h-5 text-surface-400" />
                  </div>
                  <div>
                    <h3 className="font-medium text-surface-100">
                      {doc.filename}
                    </h3>
                    <p className="text-surface-500 text-xs">
                      {format(new Date(doc.created_at), 'MMM d, yyyy h:mm a')} •{' '}
                      {doc.format.toUpperCase()}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleDownload(doc)}
                    className="p-2 text-surface-400 hover:text-surface-200 transition-colors"
                    title="Download"
                  >
                    <ArrowDownTrayIcon className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => handleDelete(doc.id)}
                    className="p-2 text-surface-400 hover:text-red-400 transition-colors"
                    title="Delete"
                  >
                    <TrashIcon className="w-5 h-5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
